import React, { useEffect } from "react";
import { NavLink, useLocation, Link } from "react-router-dom";
import {
  ArrowLeft, Menu, PanelLeftClose, PanelLeftOpen, X,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { moduleByKey } from "@/modules/registry";
import { useSidebar } from "@/hooks/useSidebar";

/**
 * Module workspace shell with a global, collapsible side panel.
 *
 * Desktop (lg+):
 *   - Sidebar is inline. Users can collapse it to a 68px icon-only rail
 *     via the header toggle. Preference is persisted in localStorage
 *     (`owl.sidebar.collapsed`) so it survives refresh, login, and
 *     module switches.
 *   - When collapsed, hovering any nav item shows a floating tooltip.
 *
 * Mobile / Tablet (<lg):
 *   - Sidebar is hidden by default and opens as an overlay drawer with
 *     a backdrop. Route changes auto-close the drawer.
 *
 * Focus modules (`hideSidebar: true`) or modules with a single nav item
 * bypass this entirely, matching the previous behaviour.
 */
export default function ModuleShell({ moduleKey, children }) {
  const mod = moduleByKey(moduleKey);
  const { hasPerm, user, activeMembership } = useAuth();
  const location = useLocation();
  const {
    collapsed, mobileOpen, toggleCollapsed, openMobile, closeMobile,
  } = useSidebar();

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    closeMobile();
  }, [location.pathname, closeMobile]);

  if (!mod) return children;

  const items = (mod.items || []).filter((it) => !it.perm || hasPerm(it.perm));
  if (mod.hideSidebar || items.length <= 1) {
    return (
      <div className="h-full w-full overflow-y-auto surface" data-testid={`module-shell-${mod.key}`}>
        {children}
      </div>
    );
  }

  const firstName = (user?.name || user?.email || "Operator").split(" ")[0];
  const initials = (user?.name || user?.email || "?")
    .split(/\s+/)
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const role =
    user?.platform_role === "oneb_super_admin"
      ? "Super admin"
      : (activeMembership?.role || "member").replace(/_/g, " ");

  return (
    <div className="h-full w-full flex overflow-hidden surface" data-testid={`module-shell-${mod.key}`}>
      {/* ==================================================== Desktop sidebar */}
      <aside
        data-testid={`module-sidebar-${mod.key}`}
        data-collapsed={collapsed ? "true" : "false"}
        aria-label={`${mod.name} navigation`}
        className={`hidden lg:flex h-full shrink-0 flex-col border-r hairline-soft surface-2 relative
          transition-[width] duration-200 ease-out will-change-[width]
          ${collapsed ? "w-[68px]" : "w-[240px]"}`}
      >
        <SidebarInner
          mod={mod}
          items={items}
          collapsed={collapsed}
          onToggle={toggleCollapsed}
          firstName={firstName}
          initials={initials}
          role={role}
          testIdPrefix=""
        />
      </aside>

      {/* ==================================================== Mobile overlay */}
      {/* Backdrop */}
      <div
        aria-hidden
        onClick={closeMobile}
        className={`lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]
          transition-opacity duration-200 ${
            mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
      />
      <aside
        data-testid={`module-sidebar-mobile-${mod.key}`}
        aria-label={`${mod.name} navigation`}
        aria-hidden={!mobileOpen}
        className={`lg:hidden fixed top-16 bottom-0 left-0 z-50 w-[280px] max-w-[85vw]
          border-r hairline surface-2 flex flex-col
          transition-transform duration-200 ease-out will-change-transform
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <SidebarInner
          mod={mod}
          items={items}
          collapsed={false}
          onToggle={null}
          firstName={firstName}
          initials={initials}
          role={role}
          onClose={closeMobile}
          mobile
          testIdPrefix="mobile-"
        />
      </aside>

      {/* ==================================================== Content */}
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden surface transition-all duration-200">
        {/* Mobile module bar with hamburger */}
        <div
          className="lg:hidden shrink-0 h-11 flex items-center gap-2 px-3 border-b hairline surface-card"
          data-testid={`module-mobilebar-${mod.key}`}
        >
          <button
            type="button"
            onClick={openMobile}
            data-testid="module-sidebar-open"
            aria-label={`Open ${mod.name} navigation`}
            aria-expanded={mobileOpen}
            className="h-8 w-8 grid place-items-center rounded-md ink-muted hover:ink hover:bg-foreground/[0.06] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
          >
            <Menu size={16} />
          </button>
          <span className="text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">
            {mod.name}
          </span>
        </div>

        <div className="flex-1 min-w-0 overflow-y-auto surface">
          {children}
        </div>
      </div>
    </div>
  );
}

/* ==================================================================== */
/* Sidebar inner content (shared between desktop rail and mobile drawer) */
/* ==================================================================== */

function SidebarInner({
  mod, items, collapsed, onToggle,
  firstName, initials, role, onClose, mobile = false, testIdPrefix = "",
}) {
  return (
    <>
      {/* Top row: toggle (desktop) or close (mobile) */}
      <div
        className={`shrink-0 flex items-center h-11 border-b hairline-soft
          ${collapsed ? "justify-center px-2" : "justify-between px-4"}`}
      >
        {!collapsed && (
          <span className="text-[10.5px] uppercase tracking-[0.22em] font-semibold ink-subtle truncate">
            {mod.name}
          </span>
        )}
        {mobile ? (
          <button
            type="button"
            onClick={onClose}
            data-testid="module-sidebar-close"
            aria-label="Close navigation"
            className="h-7 w-7 grid place-items-center rounded-md ink-muted hover:ink hover:bg-foreground/[0.06] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
          >
            <X size={14} />
          </button>
        ) : (
          <button
            type="button"
            onClick={onToggle}
            data-testid="module-sidebar-toggle"
            aria-label={collapsed ? "Expand side panel" : "Collapse side panel"}
            aria-expanded={!collapsed}
            title={collapsed ? "Expand side panel" : "Collapse side panel"}
            className="h-7 w-7 grid place-items-center rounded-md ink-muted hover:ink hover:bg-foreground/[0.06] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
          >
            {collapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
          </button>
        )}
      </div>

      {/* User block */}
      <div className={`pt-6 pb-6 ${collapsed ? "px-2" : "px-6"}`}>
        <div className={`flex items-center gap-3 ${collapsed ? "justify-center" : ""}`}>
          <div className="h-11 w-11 rounded-full grid place-items-center border hairline surface-card shrink-0">
            <span className="ink text-[13px] font-semibold tracking-wide">{initials}</span>
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <div className="ink text-[15px] font-semibold truncate leading-tight">{firstName}</div>
              <div className="tiny-label mt-1 truncate">{role}</div>
            </div>
          )}
        </div>
      </div>

      {/* Nav */}
      <nav
        className={`flex-1 pt-2 pb-6 space-y-0.5 overflow-y-auto overflow-x-hidden
          ${collapsed ? "px-2" : "px-4"}`}
      >
        <SidebarItem
          to="/"
          icon={ArrowLeft}
          label="All modules"
          collapsed={collapsed}
          testId={`${testIdPrefix}module-back-${mod.key}`}
          plain
        />

        <div className={`h-px bg-border/60 my-3 ${collapsed ? "mx-1" : "mx-3"}`} />

        {items.map((it) => (
          <SidebarNavItem
            key={it.path}
            item={it}
            mod={mod}
            collapsed={collapsed}
            testIdPrefix={testIdPrefix}
          />
        ))}
      </nav>

      {/* Module footer */}
      {!collapsed && (
        <div className="px-6 py-5 border-t hairline-soft">
          <div className="tiny-label">Module</div>
          <div className="mt-2 text-xs ink-muted leading-relaxed">{mod.tagline}</div>
        </div>
      )}
    </>
  );
}

/* ---------------- Nav item primitives ---------------- */

function SidebarItem({ to, icon: Icon, label, collapsed, testId, plain }) {
  return (
    <div className="relative group">
      <Link
        to={to}
        data-testid={testId}
        className={`h-11 flex items-center gap-3 rounded-md transition-colors
          ${collapsed ? "justify-center px-2" : "px-3"}
          ${plain ? "ink-subtle hover:ink" : "ink-subtle hover:ink"}`}
      >
        <div className="h-7 w-7 grid place-items-center rounded-md border hairline-soft shrink-0">
          <Icon size={13} />
        </div>
        {!collapsed && (
          <span className="text-[11px] uppercase tracking-[0.24em] font-semibold truncate">
            {label}
          </span>
        )}
      </Link>
      {collapsed && <NavTooltip label={label} />}
    </div>
  );
}

function SidebarNavItem({ item, mod, collapsed, testIdPrefix = "" }) {
  const Icn = item.icon;
  const testId = `${testIdPrefix}module-nav-${mod.key}-${item.label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <div className="relative group">
      <NavLink
        to={item.path}
        end={!!item.end}
        data-testid={testId}
        className={({ isActive }) =>
          `h-11 flex items-center gap-3 rounded-md transition-colors
          ${collapsed ? "justify-center px-2" : "px-3"}
          ${isActive ? "ink" : "ink-subtle hover:ink"}`
        }
      >
        {({ isActive }) => (
          <>
            <div
              className={`h-7 w-7 grid place-items-center rounded-md border transition-colors shrink-0 ${
                isActive
                  ? "border-foreground/50 bg-foreground/[0.04]"
                  : "border-transparent group-hover:border-border"
              }`}
            >
              <Icn size={13} />
            </div>
            {!collapsed && (
              <>
                <span className="text-[11px] uppercase tracking-[0.24em] font-semibold flex-1 truncate">
                  {item.label}
                </span>
                {isActive && <span className="h-1 w-1 rounded-full bg-foreground/70" />}
              </>
            )}
          </>
        )}
      </NavLink>
      {collapsed && <NavTooltip label={item.label} />}
    </div>
  );
}

/** Floating tooltip shown when the sidebar is collapsed. */
function NavTooltip({ label }) {
  return (
    <span
      role="tooltip"
      className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 z-50
        whitespace-nowrap px-2.5 py-1.5 rounded-md
        bg-slate-900 ring-1 ring-white/10 text-[10.5px] uppercase tracking-[0.22em] font-semibold ink
        shadow-[0_8px_24px_-6px_rgba(0,0,0,0.6)]
        opacity-0 -translate-x-1
        group-hover:opacity-100 group-hover:translate-x-0
        transition-all duration-150 delay-75"
    >
      {label}
    </span>
  );
}
